import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Loader2,
  FileText,
  TrendingUp,
  Lightbulb,
  Building2,
} from 'lucide-react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import api from '../../lib/api';
import { DepartmentSummary } from '../../types/budget';
import EmptyState from '../EmptyState';
import StatCard, { StatCardRow } from '../StatCard';

const FUND_COLORS: Record<string, string> = {
  GF: '#6366f1',
  CF: '#14b8a6',
  RF: '#f97316',
  FF: '#8b5cf6',
};

const FUND_LABELS: Record<string, string> = {
  GF: 'General Fund',
  CF: 'Cash Funds',
  RF: 'Reappropriated',
  FF: 'Federal Funds',
};

function formatCurrency(amount: number): string {
  if (Math.abs(amount) >= 1_000_000_000) {
    return `$${(amount / 1_000_000_000).toFixed(1)}B`;
  }
  if (Math.abs(amount) >= 1_000_000) {
    return `$${(amount / 1_000_000).toFixed(1)}M`;
  }
  if (Math.abs(amount) >= 1_000) {
    return `$${(amount / 1_000).toFixed(0)}K`;
  }
  return `$${amount.toFixed(0)}`;
}

interface DepartmentCardProps {
  summary: DepartmentSummary;
  onClick: () => void;
}

const DepartmentCard: React.FC<DepartmentCardProps> = ({ summary, onClick }) => {
  const { department, fundingSummary, totalAmount, itemCount, documentCount, initiatives } = summary;

  const pieData = fundingSummary
    .filter(f => ['GF', 'CF', 'RF', 'FF'].includes(f.fund_type) && f.total_amount !== 0)
    .map(f => ({
      name: FUND_LABELS[f.fund_type] || f.fund_type,
      value: Math.abs(f.total_amount),
      fund_type: f.fund_type,
    }));

  const hasPieData = pieData.length > 0 && pieData.some(d => d.value > 0);

  return (
    <div
      onClick={onClick}
      className="card p-5 cursor-pointer hover:shadow-md transition-all hover:-translate-y-0.5"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-text-primary dark:text-text-dark-primary truncate">
            {department.dept_name}
          </h3>
          <span className="inline-block mt-1 px-2 py-0.5 text-xs font-medium rounded bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300">
            {department.dept_code}
          </span>
        </div>
        <div className="text-right ml-3 flex-shrink-0">
          <p className="text-lg font-bold text-text-primary dark:text-text-dark-primary">
            {formatCurrency(totalAmount)}
          </p>
          <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary">
            {itemCount} item{itemCount !== 1 ? 's' : ''}
          </p>
        </div>
      </div>

      {hasPieData && (
        <div className="flex items-center gap-3 mb-3">
          <div className="w-24 h-24 flex-shrink-0">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={pieData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={20}
                  outerRadius={40}
                  isAnimationActive={false}
                >
                  {pieData.map((entry) => (
                    <Cell key={entry.fund_type} fill={FUND_COLORS[entry.fund_type] || '#94a3b8'} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value: number) => [formatCurrency(value), '']}
                  contentStyle={{
                    backgroundColor: 'var(--bg-primary, #fff)',
                    border: '1px solid var(--border-light, #e5e7eb)',
                    borderRadius: '8px',
                    fontSize: '12px',
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex-1 space-y-1">
            {pieData.map((entry) => (
              <div key={entry.fund_type} className="flex items-center gap-2 text-xs">
                <span
                  className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: FUND_COLORS[entry.fund_type] || '#94a3b8' }}
                />
                <span className="text-text-secondary dark:text-text-dark-secondary truncate">
                  {FUND_LABELS[entry.fund_type] || entry.fund_type}
                </span>
                <span className="ml-auto font-medium text-text-primary dark:text-text-dark-primary">
                  {formatCurrency(entry.value)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {initiatives.length > 0 && (
        <div className="mb-3">
          <div className="flex items-center gap-1.5 mb-1.5">
            <Lightbulb className="w-3.5 h-3.5 text-amber-500" />
            <span className="text-xs font-medium text-text-secondary dark:text-text-dark-secondary">
              Key Initiatives
            </span>
          </div>
          <ul className="space-y-1">
            {initiatives.slice(0, 3).map((init) => (
              <li key={init.id} className="text-xs text-text-secondary dark:text-text-dark-secondary line-clamp-2 pl-3 relative before:absolute before:left-0 before:top-1.5 before:w-1.5 before:h-1.5 before:rounded-full before:bg-primary-400">
                {init.initiative_text}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex items-center justify-between pt-3 border-t border-border-light dark:border-border-dark">
        <div className="flex items-center gap-1.5 text-xs text-text-tertiary dark:text-text-dark-tertiary">
          <FileText className="w-3.5 h-3.5" />
          <span>{documentCount} document{documentCount !== 1 ? 's' : ''}</span>
        </div>
        <span className="text-xs font-medium text-primary-600 dark:text-primary-400">
          View Details &rarr;
        </span>
      </div>
    </div>
  );
};

const DepartmentCardGrid: React.FC = () => {
  const navigate = useNavigate();
  const [summaries, setSummaries] = useState<DepartmentSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchSummaries = async () => {
      try {
        setIsLoading(true);
        const response = await api.get('/budget/departments/summary');
        if (response.data?.success) {
          setSummaries(response.data.data);
        }
      } catch (err) {
        setError('Failed to load department summaries');
      } finally {
        setIsLoading(false);
      }
    };
    fetchSummaries();
  }, []);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-16">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (error) {
    return (
      <EmptyState hint={error} />
    );
  }

  if (summaries.length === 0) {
    return (
      <div className="text-center py-12">
        <Building2 className="w-12 h-12 mx-auto mb-3 text-text-tertiary dark:text-text-dark-tertiary" />
        <p className="text-text-tertiary dark:text-text-dark-tertiary">
          No department data available.
        </p>
      </div>
    );
  }

  const totalBudget = summaries.reduce((sum, s) => sum + s.totalAmount, 0);
  const totalItems = summaries.reduce((sum, s) => sum + s.itemCount, 0);
  const totalDocs = summaries.reduce((sum, s) => sum + s.documentCount, 0);

  return (
    <div className="space-y-6">
      <StatCardRow>
        <StatCard value={summaries.length} label="Departments" icon={Building2} />
        <StatCard
          value={formatCurrency(totalBudget)}
          label="Total Budget"
          variant="success"
          icon={TrendingUp}
          sublabel={`${totalItems} Budget Items`}
        />
        <StatCard value={totalDocs} label="JBC Documents" variant="info" icon={FileText} />
      </StatCardRow>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {summaries.map((summary) => (
          <DepartmentCard
            key={summary.department.id}
            summary={summary}
            onClick={() => navigate(`/budget/departments/${summary.department.id}`)}
          />
        ))}
      </div>
    </div>
  );
};

export default DepartmentCardGrid;
